import { useBooking } from './ctx'
import { Ic } from './fields'
import type { FinalState } from './ReviewStep'
import { formKind, naira, num } from './rules'
import type { Price } from './types'

/**
 * `exact` is false while the estimate is priced on the route alone (an address
 * is still missing). `waiting` is a failed estimate while details are still
 * coming in — shown as "waiting for…", never as an error.
 */
export type EstimateState =
  | { status: 'idle' }
  | { status: 'loading'; price: Price | null }
  | { status: 'ok'; price: Price; exact: boolean }
  | { status: 'waiting'; price: Price | null }
  | { status: 'error'; message: string }

/** One thing the price depends on: `label` reads in a sentence, `short` sits in a pill. */
export type PriceInput = { key: string; label: string; short: string; done: boolean }

const fixCap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '')

function Lines({ price }: { price: Price }) {
  const b = price.breakDown || ({} as Price['breakDown'])
  return (
    <dl className='tk-lines'>
      {b.grossWeight != null && (
        <div>
          <dt>Charged weight</dt>
          <dd>{num(b.grossWeight).toFixed(1)} kg</dd>
        </div>
      )}
      <div>
        <dt>Shipping</dt>
        <dd>{naira(num(price.shippingCost))}</dd>
      </div>
      {(b.extraCharges || []).map((c, n) => (
        <div key={n}>
          <dt>{fixCap(c.name)}</dt>
          <dd>{naira(num(c.price))}</dd>
        </div>
      ))}
      {num(b.addOnsCost) > 0 && (
        <div>
          <dt>Packaging</dt>
          <dd>{naira(num(b.addOnsCost))}</dd>
        </div>
      )}
      {num(price.insurance) > 0 && (
        <div>
          <dt>Protection</dt>
          <dd>{naira(num(price.insurance))}</dd>
        </div>
      )}
      {num(b.discountAmount) > 0 && (
        <div>
          <dt>Discount</dt>
          <dd>−{naira(num(b.discountAmount))}</dd>
        </div>
      )}
    </dl>
  )
}

const joinLabels = (xs: string[]) =>
  xs.length <= 1 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`

// What has to be in before any price can be shown; the addresses only firm it up
const PRICE_NEEDS = ['customer', 'route', 'parcel']

/** "Waiting for the receiver’s address" with a checklist of what the price needs. */
function Waiting({ inputs, firm }: { inputs: PriceInput[]; firm: boolean }) {
  const shown = firm ? inputs : inputs.filter((i) => PRICE_NEEDS.includes(i.key))
  const missing = shown.filter((i) => !i.done)
  if (!missing.length) return null
  return (
    <div className='tk-wait' role='status'>
      <div className='tk-wait-h'>
        <span className='tk-wait-ic' aria-hidden='true'>
          <Ic name='hourglass' />
        </span>
        <div>
          <b>Waiting for {joinLabels(missing.map((m) => m.label.toLowerCase()))}</b>
          <span>
            {firm
              ? 'The price above is for the route. It firms up once the addresses are in.'
              : 'The price appears as soon as these are in.'}
          </span>
        </div>
      </div>
      <ul className='tk-check' aria-label='What the price needs'>
        {shown.map((i) => (
          <li key={i.key} className={i.done ? 'done' : ''}>
            <Ic name={i.done ? 'circle-check' : 'circle-dashed'} />
            <span>{i.short}</span>
            <span className='bk-sr'>{i.done ? ', added' : ', still needed'}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function priceShown(estimate: EstimateState, final: FinalState, onReview: boolean) {
  if (onReview) {
    if (final?.status === 'ok') return { label: 'Final price', value: naira(num(final.price.totalPrice)) }
    if (final?.status === 'error') return { label: 'Price unavailable', value: '—' }
    return { label: 'Getting final price…', value: '—' }
  }
  if (estimate.status === 'ok')
    return { label: estimate.exact ? 'Estimated total' : 'Route estimate', value: naira(num(estimate.price.totalPrice)) }
  if (estimate.status === 'loading' && estimate.price)
    return { label: 'Updating estimate…', value: naira(num(estimate.price.totalPrice)) }
  if (estimate.status === 'waiting' && estimate.price)
    return { label: 'Last estimate', value: naira(num(estimate.price.totalPrice)) }
  if (estimate.status === 'waiting' || estimate.status === 'idle') return { label: 'Waiting for details', value: '—' }
  return { label: 'Estimated total', value: '—' }
}

export default function Ticket({
  estimate,
  final,
  onReview,
  onRetry,
  inputs,
  headingLevel,
}: {
  estimate: EstimateState
  final: FinalState
  onReview: boolean
  onRetry: () => void
  inputs: PriceInput[]
  headingLevel?: boolean
}) {
  const { f, countries } = useBooking()
  const k = formKind(f)
  const name = (cc: string) => countries.find((c) => c.isoCode === cc)?.name || ''
  let fc = f.from || '—'
  let tc = f.to || '—'
  let fn = name(f.from) || 'From'
  let tn = name(f.to) || 'Where to?'
  if (k === 'domestic') {
    fc = f.fromState || 'NG'
    tc = f.toState || 'NG'
    fn = f.sender.addr.city || 'Nigeria'
    tn = f.receiver.addr.city || 'Nigeria'
  }
  const long = fc.length > 3 || tc.length > 3
  const kindLabel = k && k !== 'invalid' ? { domestic: 'Domestic', export: 'Export', import: 'Import' }[k] : ''

  const estimateBody = (price: Price, label: string, stale: boolean, fine: string) => (
    <div className={stale ? 'tk-stale' : ''}>
      <Lines price={price} />
      <div className='tk-total'>
        <span>{label}</span>
        <b>{naira(num(price.totalPrice))}</b>
      </div>
      {fine && <p className='tk-fine'>{fine}</p>}
    </div>
  )

  let body
  if (k === 'invalid')
    body = (
      <div className='bk-note bad'>
        <Ic name='circle-alert' />
        <p>We can’t price this route. One end must be Nigeria.</p>
      </div>
    )
  else if (onReview && final?.status === 'loading')
    body = (
      <>
        <p className='tk-empty row'>
          <Ic name='loader-circle' className='spin' /> Getting the final price…
        </p>
        <div className='bk-skel-wrap' aria-hidden='true'>
          <div className='bk-skel' style={{ width: '80%' }} />
          <div className='bk-skel' style={{ width: '40%', height: 22 }} />
        </div>
      </>
    )
  else if (onReview && final?.status === 'error')
    body = (
      <div className='bk-note bad'>
        <Ic name='circle-alert' />
        <p>
          We couldn’t get the final price.{' '}
          <button type='button' className='bk-linkbtn inline' onClick={onRetry}>
            Try again
          </button>
        </p>
      </div>
    )
  else if (onReview && final?.status === 'ok')
    body = estimateBody(
      final.price,
      'Final price',
      false,
      'This is what the customer is charged. Duties and taxes at the destination are paid by the receiver.',
    )
  else if (estimate.status === 'idle') body = <Waiting inputs={inputs} firm={false} />
  else if (estimate.status === 'waiting')
    body = (
      <>
        {estimate.price && estimateBody(estimate.price, 'Last estimate', true, '')}
        <Waiting inputs={inputs} firm={!!estimate.price} />
      </>
    )
  else if (estimate.status === 'error')
    body = (
      <div className='bk-note warn'>
        <Ic name='info' />
        <p>
          We couldn’t estimate these addresses yet. You can carry on — the price is checked again on the last step.
          {estimate.message && <span className='tk-detail'>Pricing said: {estimate.message}</span>}
        </p>
      </div>
    )
  else if (estimate.status === 'loading' && !estimate.price)
    body = (
      <div className='bk-skel-wrap' aria-hidden='true'>
        <div className='bk-skel' style={{ width: '60%' }} />
        <div className='bk-skel' style={{ width: '80%' }} />
        <div className='bk-skel' style={{ width: '40%', height: 22 }} />
      </div>
    )
  else {
    const price = estimate.status === 'ok' ? estimate.price : (estimate.price as Price)
    const exact = estimate.status === 'ok' && estimate.exact
    body = (
      <>
        {estimateBody(
          price,
          exact ? 'Estimated total' : 'Route estimate',
          estimate.status === 'loading',
          exact ? 'Estimate — the exact price is checked on the last step.' : '',
        )}
        {!exact && <Waiting inputs={inputs} firm />}
      </>
    )
  }

  return (
    <div className='bk-ticket'>
      <div className='tk-top'>
        <p className='tk-eye' {...(headingLevel ? { role: 'heading', 'aria-level': 2 } : {})}>
          Shipment
        </p>
        <div className='tk-route'>
          <div className='tk-end'>
            <span className={`tk-code ${long ? 'long' : ''}`}>{fc}</span>
            <span className='tk-place'>{fn}</span>
          </div>
          <div className='tk-mid' aria-hidden='true'>
            <i />
            <Ic name={k === 'domestic' ? 'truck' : 'plane'} />
            <i />
          </div>
          <div className='tk-end to'>
            <span className={`tk-code ${long ? 'long' : ''}`}>{tc}</span>
            <span className='tk-place'>{tn}</span>
          </div>
        </div>
        {kindLabel && (
          <span className='tk-kind'>
            {kindLabel} · {f.type === 'document' ? 'Documents' : 'Package'}
          </span>
        )}
      </div>
      <div className='tk-perf' aria-hidden='true' />
      <div className='tk-body'>{body}</div>
    </div>
  )
}
